import { startOfTomorrow } from 'date-fns/startOfTomorrow';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';

import type {
    AccountNumber,
    BankDetails,
    BicCode,
    ChargeBearerOptions,
    CountryCode,
    CrossBorderAccountType,
    CrossBorderPriority,
    CrossBorderReceiver,
    Currency,
    IbanRequirements,
    NationalId,
    NationalIdRequirements,
    TransferCurrency,
} from '../../../../generated-types';

export interface CrossBorderPaymentState {
    fromAccount?: AccountNumber;
    receiver?: SelectCrossBorderReceiver;
    receiverName?: string;
    amount?: number;
    currency?: Currency;
    priority?: CrossBorderPriority;
    chargeBearer?: ChargeBearerOptions;
    transferCurrency?: Currency;
    paymentDate?: ZonedDate;
    firstBankingDateFromToday?: ZonedDate;
    regulatoryCode?: string;
    paymentDescription?: string;
    messageReceiver?: string;
    country?: CrossBorderCountry;
    bankValidation?: BankValidationDetails;
    amountInNok?: number;
    newReceiver?: boolean;
    chosenReceiver?: CrossBorderReceiver;
}

export interface SelectCrossBorderReceiver {
    name?: string;
    accountNumber?: string;
    accountType?: CrossBorderAccountType;
    address?: string;
    address2?: string;
    postcode?: string;
    town?: string;
    receiverCountryCode?: CountryCode;
    bankCountryCode?: CountryCode;
    nationalId?: NationalId;
    bic?: BicCode;
    needBic?: boolean;
    bankValidationOption?: BankValidationOptions;
}

export interface CrossBorderCountry {
    bban?: string[];
    chargeBearers?: ChargeBearerOptions[];
    countryCode?: CountryCode;
    currency?: Currency;
    ibanLength?: number;
    ibanRequirements?: IbanRequirements;
    name?: string;
    nationalIdRequirements?: NationalIdRequirements;
    partOfSepa?: boolean;
    requiresBic?: boolean;
    requiresCreditorDetails?: boolean;
    transferCurrencies?: TransferCurrency[];
    validForPayments?: boolean;
}

export interface BankValidationDetails {
    bank?: BankDetails;
    routingBicBank?: BankDetails;
    valid: boolean;
}

export enum BankValidationOptions {
    BIC = 'BIC',
    BANKCODE = 'BANKCODE',
}

export const MAX_ADDRESS_LENGTH = 70;
export const GB_COUNTRY_CODE = 'GB';
export const GB_CURRENCY = 'GBP';

const tomorrow = getZonedDate(startOfTomorrow());

export const crossBorderPaymentInitialState: CrossBorderPaymentState = {
    amount: undefined,
    country: undefined,
    currency: undefined,
    priority: 'STANDARD',
    chargeBearer: 'SHARED',
    transferCurrency: undefined,
    fromAccount: undefined,
    receiverName: '',
    messageReceiver: '',
    paymentDescription: '',
    regulatoryCode: undefined,
    paymentDate: undefined,
    firstBankingDateFromToday: tomorrow,
    receiver: undefined,
    bankValidation: undefined,
};

export const toSelectCrossBorderReceiver = (receiver: CrossBorderReceiver): SelectCrossBorderReceiver => {
    return {
        name: receiver.name,
        accountNumber: receiver.account.number,
        accountType: receiver.account.type,
        address: receiver.address,
        address2: receiver.address2,
        town: receiver.town,
        postcode: receiver.postcode,
        receiverCountryCode: receiver.countryCode,
        bankCountryCode: receiver.bank.countryCode,
        nationalId: receiver.bank.nationalId,
        bic: receiver.bank.bic,
        needBic: false,
        bankValidationOption: BankValidationOptions.BANKCODE,
    };
};
